import {
  Component,
  Suspense,
  lazy,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  ComponentType,
  ErrorInfo,
  ReactNode,
} from 'react';
import { useScreenNavigation } from '../../hooks/useScreenNavigation';
import { useLanguage } from '../../i18n';
import {
  isExpiredLifecycleError,
  isRateLimitedLifecycleError,
  verificationLifecycleService,
} from '../../services/VerificationLifecycleService';
import type { ScreenType } from '../../types/screens';
import { logger } from '../../utils/logger';
import { NeutralSpinner } from './NeutralSpinner';
import { VerificationScreenTemplate } from './VerificationScreenTemplate';

const screenLoadingLogger = logger.clone({
  labels: { component: 'RecoverableLazyScreen' },
});

export type LazyScreenLoader = () => Promise<{
  default: ComponentType;
}>;

const SCREEN_LOADING_RELOAD_GUARD = 'sp-screen-loading-reload';

interface ScreenLoadingReloadGuard {
  sessionId: string;
  screen: ScreenType;
}

const readReloadGuard = (): ScreenLoadingReloadGuard | null => {
  try {
    const stored = window.sessionStorage.getItem(SCREEN_LOADING_RELOAD_GUARD);
    return stored ? JSON.parse(stored) as ScreenLoadingReloadGuard : null;
  } catch {
    return null;
  }
};

const writeReloadGuard = (guard: ScreenLoadingReloadGuard): boolean => {
  try {
    window.sessionStorage.setItem(
      SCREEN_LOADING_RELOAD_GUARD,
      JSON.stringify(guard),
    );
    return true;
  } catch {
    // Never reload without a durable guard: restricted storage must not create
    // a page reload loop.
    return false;
  }
};

const clearReloadGuard = (screen: ScreenType): void => {
  const guard = readReloadGuard();
  if (guard?.screen !== screen) return;
  try {
    window.sessionStorage.removeItem(SCREEN_LOADING_RELOAD_GUARD);
  } catch {
    // Loading succeeded; storage cleanup is best-effort only.
  }
};

const reloadCurrentPage = (): void => window.location.reload();

export class ScreenLoadingError extends Error {
  readonly screen: ScreenType;
  readonly generation: number;
  readonly originalError: unknown;

  constructor(screen: ScreenType, generation: number, originalError: unknown) {
    super('Verification screen could not be loaded');
    this.name = 'ScreenLoadingError';
    this.screen = screen;
    this.generation = generation;
    this.originalError = originalError;
  }
}

interface ScreenLoadingBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  onLoadingError: (error: ScreenLoadingError) => void;
}

interface ScreenLoadingBoundaryState {
  error: ScreenLoadingError | null;
}

class ScreenLoadingBoundary extends Component<
  ScreenLoadingBoundaryProps,
  ScreenLoadingBoundaryState
> {
  state: ScreenLoadingBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ScreenLoadingBoundaryState {
    if (!(error instanceof ScreenLoadingError)) {
      throw error;
    }
    return { error };
  }

  componentDidCatch(error: Error, _errorInfo: ErrorInfo): void {
    if (!(error instanceof ScreenLoadingError)) return;
    screenLoadingLogger.warn('Verification screen asset could not be loaded', {
      labels: { screen: error.screen },
    });
    this.props.onLoadingError(error);
  }

  render(): ReactNode {
    if (this.state.error) return this.props.fallback;
    return this.props.children;
  }
}

interface RecoverableLazyScreenProps {
  screen: ScreenType;
  loader: LazyScreenLoader;
  reloadPage?: () => void;
}

export function RecoverableLazyScreen({
  screen,
  loader,
  reloadPage = reloadCurrentPage,
}: RecoverableLazyScreenProps) {
  const { navigateToScreen } = useScreenNavigation();
  const { t } = useLanguage();
  const [generation, setGeneration] = useState(0);
  const [recovering, setRecovering] = useState(false);
  const recoveryRef = useRef<Promise<void> | null>(null);
  const recoverySessionIdRef = useRef<string | null>(null);

  const LazyScreen = useMemo(
    () => lazy(async () => {
      try {
        const loaded = await loader();
        clearReloadGuard(screen);
        return loaded;
      } catch (error) {
        throw new ScreenLoadingError(screen, generation, error);
      }
    }),
    [generation, loader, screen],
  );

  const retryCurrentScreen = useCallback(() => {
    setGeneration((value) => value + 1);
  }, []);

  const navigateOrRetry = useCallback((target: ScreenType) => {
    if (target === screen) {
      retryCurrentScreen();
      return;
    }
    navigateToScreen(target);
  }, [navigateToScreen, retryCurrentScreen, screen]);

  const recover = useCallback(async () => {
    const initial = await verificationLifecycleService.reconcile();
    recoverySessionIdRef.current = initial.sessionId;
    const initialTarget = verificationLifecycleService.screenFor(initial);

    if (initialTarget === screen) {
      retryCurrentScreen();
      return;
    }

    if (
      initial.sessionStatus !== 'PENDING' ||
      initial.terminal ||
      initial.recovery.action !== 'CONTINUE'
    ) {
      navigateToScreen(initialTarget);
      return;
    }

    try {
      await verificationLifecycleService.alignToScreen(screen);
    } catch (error) {
      screenLoadingLogger.warn(
        'Screen transition remained uncertain during loading recovery',
        { labels: { screen, errorClass: error instanceof Error ? error.name : typeof error } },
      );
    }

    // Whether alignment succeeded or its response was lost, a fresh read is
    // the only authority for deciding where recovery continues.
    const reconciled = await verificationLifecycleService.reconcile();
    navigateOrRetry(verificationLifecycleService.screenFor(reconciled));
  }, [navigateOrRetry, navigateToScreen, retryCurrentScreen, screen]);

  const handleLoadingError = useCallback((error: ScreenLoadingError) => {
    const sessionId = recoverySessionIdRef.current;
    if (error.generation === 0 || !sessionId) return;

    const guard = readReloadGuard();
    if (guard?.sessionId === sessionId && guard.screen === screen) return;

    if (!writeReloadGuard({ sessionId, screen })) return;
    reloadPage();
  }, [reloadPage, screen]);

  const handleRetry = useCallback(() => {
    if (recoveryRef.current) return;

    setRecovering(true);
    const run = recover()
      .catch((error) => {
        if (
          isExpiredLifecycleError(error) ||
          isRateLimitedLifecycleError(error)
        ) {
          navigateOrRetry('failure');
          return;
        }
        screenLoadingLogger.warn('Unable to recover verification screen loading', {
          labels: {
            screen,
            errorClass: error instanceof Error ? error.name : typeof error,
          },
        });
      })
      .finally(() => {
        if (recoveryRef.current === run) {
          recoveryRef.current = null;
          setRecovering(false);
        }
      });
    recoveryRef.current = run;
  }, [navigateOrRetry, recover, screen]);

  const fallback = (
    <VerificationScreenTemplate
      title={t('lifecycleRecovery.loadTitle')}
      description={t('lifecycleRecovery.loadDescription')}
      showProgress={false}
      showBackButton={false}
      primaryAction={{
        label: t('common.tryAgain'),
        onClick: handleRetry,
        disabled: recovering,
        loading: recovering,
      }}
    />
  );

  return (
    <ScreenLoadingBoundary
      key={generation}
      fallback={fallback}
      onLoadingError={handleLoadingError}
    >
      <Suspense fallback={<NeutralSpinner />}>
        <LazyScreen />
      </Suspense>
    </ScreenLoadingBoundary>
  );
}
